import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { INTL_LOCALE, fmt, isLocale, type Locale } from '@quoter/shared/locale'
import type { CoreStrings } from './index'
import { getProduct } from '../config'
import { createFormatters, type Formatters } from './formatters'
import { readStoredLocale, resolveInitialLocale, writeStoredLocale } from './locale-storage'
import { useSession } from '../session/useSession'
import { useProfileLocaleMutation } from '../hooks/useProfileLocale'
import { EVENTS, track } from '../lib/analytics'

export type I18n<S extends CoreStrings = CoreStrings> = Formatters & {
  locale: Locale
  /** BCP 47 tag for anything that talks to Intl directly. */
  intlLocale: string
  t: S
  fmt: typeof fmt
  setLocale: (locale: Locale) => void
}

const LocaleContext = createContext<I18n | null>(null)

/**
 * Owns the interface language.
 *
 * Resolution: the profile row wins once it is loaded; before that (and on
 * public routes) `?lang=` → localStorage → browser language → English. An
 * explicit switch is written to localStorage and, when signed in, to the
 * profile, so it follows the user across devices.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const session = useSession()
  const { userId, save } = useProfileLocaleMutation()

  const [initial] = useState(() =>
    resolveInitialLocale({
      search: window.location.search,
      stored: readStoredLocale(),
      navigatorLanguages: navigator.languages ?? [navigator.language],
    }),
  )
  const [local, setLocal] = useState<Locale>(initial.locale)

  // A `?lang=` on the URL is a deliberate choice: keep it past the next
  // client-side navigation, which drops the query string.
  useEffect(() => {
    if (initial.explicit) writeStoredLocale(initial.locale)
  }, [initial])

  const profile =
    session.status === 'authenticated' || session.status === 'no_subscription'
      ? session.profile
      : null
  const profileLocale = isLocale(profile?.locale) ? profile.locale : null
  const locale = profileLocale ?? local

  // First sign-in with a choice already made in this browser and none on the
  // profile yet: adopt it, once, so the database becomes the source of truth.
  const adoptedRef = useRef(false)
  useEffect(() => {
    if (!profile || profile.locale !== null || adoptedRef.current) return
    const stored = readStoredLocale()
    if (!stored) return
    adoptedRef.current = true
    void save(stored)
    // `save` is stable enough for this once-only effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  useEffect(() => {
    document.documentElement.lang = INTL_LOCALE[locale]
  }, [locale])

  const setLocale = useCallback(
    (next: Locale) => {
      setLocal(next)
      writeStoredLocale(next)
      track(EVENTS.localeChanged, { locale: next })
      if (userId) void save(next)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId],
  )

  const value = useMemo<I18n>(() => {
    const t = getProduct().dictionaries[locale]
    return {
      locale,
      intlLocale: INTL_LOCALE[locale],
      t,
      fmt,
      setLocale,
      ...createFormatters(locale, t),
    }
  }, [locale, setLocale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

/** The platform's strings. The shared shell uses this; a product uses its own typed hook. */
export function useI18n(): I18n {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useI18n must be used inside <LocaleProvider>')
  return ctx
}

/**
 * The same hook, typed with a product's full dictionary:
 *
 *   export const useI18n = createUseI18n<Strings>()
 *
 * Safe because the provider serves getProduct().dictionaries, which the
 * product built with productDictionaries() — the object really has that shape.
 */
export function createUseI18n<S extends CoreStrings>(): () => I18n<S> {
  return useI18n as unknown as () => I18n<S>
}
